import React, { useState } from "react";
import Button from "../../Button";
import { applySheetChanges, DataSheet, SheetChange, SheetColumn } from "../../sheet";
import { Cell, ResearchTable } from "../../../types/bd/Research";
import classes from "./research.module.scss";

type Row = { id: number } & Record<string, Cell>;

const keyOf = (i: number) => `c${i}`;
/** Typed "1,200,000" goes back as a number; anything else stays text ("AED 85,000", "not stated"). */
const toCell = (v: unknown): Cell => {
    if (typeof v === "number") return v;
    const text = String(v ?? "").trim();
    return /^-?\d[\d,]*(\.\d+)?$/.test(text) ? Number(text.replace(/,/g, "")) : text;
};

interface ResearchTableEditorProps {
    table: ResearchTable;
    onSave: (table: { columns: string[]; rows: Cell[][] }) => Promise<boolean>;
    onCancel: () => void;
}

/**
 * Fix the AI's table by hand: change any cell, type or paste new rows at the
 * bottom, select rows to drop them. Saving keeps the columns as they are.
 */
const ResearchTableEditor: React.FC<ResearchTableEditorProps> = ({ table, onSave, onCancel }) => {
    const [rows, setRows] = useState<Row[]>(() => table.rows.map((r, n) => ({ id: n + 1, ...Object.fromEntries(table.columns.map((_c, i) => [keyOf(i), r[i] ?? ""])) }) as Row));
    const [isSaving, setIsSaving] = useState(false);
    const columns: SheetColumn<Row>[] = table.columns.map((c, i) => ({ key: keyOf(i), label: c, width: i === 0 ? 220 : 160, type: "text" }));
    const nextId = () => Math.max(0, ...rows.map((r) => r.id)) + 1;

    const handleSave = async () => {
        setIsSaving(true);
        const saved = await onSave({ columns: table.columns, rows: rows.map((r) => table.columns.map((_c, i) => toCell(r[keyOf(i)]))) });
        setIsSaving(false);
        if (saved) onCancel();
    };

    return (
        <div className={classes.tableEditor}>
            <DataSheet<Row>
                ariaLabel={`Edit table: ${table.title}`}
                columns={columns}
                rows={rows}
                getRowId={(row) => row.id}
                noun={{ one: "row", many: "rows" }}
                showRowNumbers
                onCellsChange={(changes: SheetChange[]) => setRows((prev) => applySheetChanges(prev, changes, (row) => row.id))}
                onAddRows={(added) => setRows((prev) => {
                    let id = nextId();
                    return [...prev, ...added.map((a) => ({ ...Object.fromEntries(table.columns.map((_c, i) => [keyOf(i), (a[keyOf(i)] as Cell) ?? ""])), id: id++ }) as Row)];
                })}
                newRowLabel="Type or paste here to add a row"
                selectable
                bulkActions={(selected, clear) => (
                    <Button size="xs" variant="danger" icon="trash" onClick={() => { setRows((prev) => prev.filter((r) => !selected.includes(r))); clear(); }}
                        title="Take the selected rows out of the table">{`Drop ${selected.length} row${selected.length === 1 ? "" : "s"}`}</Button>
                )}
                emptyText="No rows left: add one, or cancel."
            />
            <div className={classes.rangeActions}>
                <Button size="sm" icon="check-lg" disabled={isSaving || !rows.length} onClick={handleSave} title="Keep these changes on the page, its range check and its downloads">{isSaving ? "Saving…" : "Save table"}</Button>
                <Button size="sm" variant="ghost" onClick={onCancel} title="Close without saving">Cancel</Button>
                <span className={classes.muted}>The table is marked "Edited" after you save; the AI's original isn't kept.</span>
            </div>
        </div>
    );
};

export default ResearchTableEditor;
